import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { ArrowRight, Building2, Home, ShoppingBag, Sparkles, Layers } from 'lucide-react';
import SEO from '../components/SEO';
import axios from 'axios';
import { 
  PageHeader, 
  Section, 
  Container, 
  FadeInOnScroll,
  PrimaryButton,
  ResponsiveImage
} from '../components/DesignSystem';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const iconMap = {
  ShoppingBag: ShoppingBag,
  Building2: Building2,
  Home: Home,
  Sparkles: Sparkles,
};

const Sectors = () => {
  const [sectors, setSectors] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchSectors();
  }, []);

  const fetchSectors = async () => {
    try {
      const response = await axios.get(`${API}/sectors`);
      setSectors(response.data.sectors || []);
    } catch (error) {
      console.error('Error fetching sectors:', error);
    } finally {
      setLoading(false);
    }
  };

  // Helper to add cache-busting to image URLs
  const getImageUrl = (sector) => {
    if (!sector.image) return 'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?crop=entropy&cs=srgb&fm=jpg&q=85';
    const baseUrl = sector.image;
    if (sector.updatedAt) {
      const separator = baseUrl.includes('?') ? '&' : '?';
      return `${baseUrl}${separator}v=${new Date(sector.updatedAt).getTime()}`;
    }
    return baseUrl;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto"></div>
          <p className="mt-4 text-gray-600 font-medium">Loading sectors...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <SEO 
        title="Sectors"
        description="FURD Ltd provides MEP engineering services across Retail, Commercial Offices, Residential, and Leisure sectors in the UK construction industry."
        keywords="MEP retail, commercial office MEP, residential MEP design, leisure sector engineering, mixed-use development MEP"
      />

      {/* Hero Section */}
      <PageHeader
        badge="Industry Focus"
        badgeIcon={Layers}
        title="Our Sectors"
        subtitle="Delivering specialist MEP engineering solutions across diverse sectors of the UK construction industry."
      />

      {/* Sectors Grid */}
      <Section background="white" padding="default">
        <Container>
          {sectors.length === 0 ? (
            <div className="text-center py-12">
              <Building2 className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500 text-lg">Sectors coming soon.</p>
            </div>
          ) : (
            <div className="space-y-24 md:space-y-32">
              {sectors.filter(s => s.isVisible !== false).map((sector, index) => {
                const Icon = iconMap[sector.icon] || Building2;
                const isEven = index % 2 === 0;
                
                return (
                  <div 
                    key={sector.id}
                    className={`grid md:grid-cols-2 gap-10 lg:gap-16 items-center`}
                    data-testid={`sector-${sector.id}`}
                  >
                    {/* Image */}
                    <FadeInOnScroll direction={isEven ? 'left' : 'right'} className={`${!isEven ? 'md:order-2' : ''}`}>
                      <div className="relative rounded-3xl shadow-2xl group scroll-smooth-image">
                        <img
                          src={getImageUrl(sector)}
                          alt={sector.title}
                          className="w-full h-[350px] md:h-[450px] object-cover rounded-3xl"
                          key={`sector-img-${sector.id}-${sector.updatedAt}`}
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent rounded-3xl"></div>
                        <div className="absolute bottom-6 left-6 right-6">
                          <div className="flex items-center gap-4">
                            <div className="w-14 h-14 bg-gradient-to-br from-blue-600 to-blue-500 rounded-2xl flex items-center justify-center shadow-lg">
                              <Icon className="w-7 h-7 text-white" />
                            </div>
                            <span className="text-white font-bold text-2xl">{sector.title}</span>
                          </div>
                        </div>
                      </div>
                    </FadeInOnScroll>

                    {/* Content */}
                    <FadeInOnScroll direction={isEven ? 'right' : 'left'} delay={150} className={`${!isEven ? 'md:order-1' : ''}`}>
                      <div>
                        <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6 tracking-tight">{sector.title}</h2>
                        <p className="text-lg text-gray-500 mb-8 whitespace-pre-wrap leading-relaxed">{sector.description}</p>
                        
                        {/* Features */}
                        {sector.features && sector.features.length > 0 && (
                          <div className="mb-8">
                            <h3 className="text-sm font-bold text-blue-600 uppercase tracking-widest mb-4">Our Expertise</h3>
                            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                              {sector.features.map((feature, idx) => (
                                <li key={idx} className="flex items-center gap-3 text-gray-600">
                                  <div className="w-2 h-2 bg-gradient-to-br from-blue-600 to-blue-400 rounded-full flex-shrink-0"></div>
                                  <span className="leading-snug">{feature}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}

                        {/* Project Types */}
                        {sector.projectTypes && sector.projectTypes.length > 0 && (
                          <div className="mb-8">
                            <h3 className="text-sm font-bold text-gray-400 uppercase tracking-widest mb-4">Project Types</h3>
                            <div className="flex flex-wrap gap-2">
                              {sector.projectTypes.map((project, idx) => (
                                <span 
                                  key={idx}
                                  className="px-4 py-2 bg-gray-100 text-gray-600 rounded-full text-sm font-medium"
                                >
                                  {project}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}

                        <Link to="/projects" className="group inline-flex items-center gap-2 text-blue-600 font-semibold hover:gap-3 transition-all relative">
                          View Related Projects
                          <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                          <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-blue-600 group-hover:w-full transition-all duration-300" />
                        </Link>
                      </div>
                    </FadeInOnScroll>
                  </div>
                );
              })}
            </div>
          )}
        </Container>
      </Section>

      {/* CTA Section */}
      <Section background="dark" padding="default">
        <Container className="text-center relative z-10">
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            <div className="absolute -top-[200px] -right-[200px] w-[500px] h-[500px] bg-blue-500/20 rounded-full blur-3xl" />
            <div className="absolute -bottom-[150px] -left-[150px] w-[400px] h-[400px] bg-blue-400/10 rounded-full blur-3xl" />
          </div>
          
          <FadeInOnScroll>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6 tracking-tight">Have a Project in Mind?</h2>
            <p className="text-lg md:text-xl text-blue-100/80 mb-10 max-w-2xl mx-auto leading-relaxed">
              Whatever your sector, our experienced team is ready to deliver MEP solutions that exceed expectations.
            </p>
            <PrimaryButton to="/contact" size="lg" icon={ArrowRight}>
              Get in Touch
            </PrimaryButton>
          </FadeInOnScroll>
        </Container>
      </Section>
    </div>
  );
};

export default Sectors;
